import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { Film } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MetricsVideo } from '@/services/dynamicFlowsService';
import { formatNumber, formatPercent, formatSeconds } from './format';
import { pickInitialVideo } from './metricsUtils';
import { VideoRetentionChart } from './VideoRetentionChart';

const th = 'border-b border-[#E5E7EB] px-2.5 py-2 font-semibold';
const td = 'px-2.5 py-2.5 text-right tabular-nums';

// Métricas por vídeo do fluxo: tabela + retenção do vídeo selecionado.
export function VideosMetrics({ videos }: { videos: MetricsVideo[] }) {
  useLanguage();
  const [selectedId, setSelectedId] = useState<string | null>(() => pickInitialVideo(videos));
  const selected =
    videos.find((video) => video.video_id === selectedId) ||
    videos.find((video) => video.video_id === pickInitialVideo(videos)) ||
    null;

  if (!videos.length) {
    return (
      <section className="rounded-2xl border border-[#D9E0EA] bg-white px-6 py-10 text-center">
        <p className="text-sm text-[#4B5563]">{translateInterfaceText("Este fluxo ainda não tem vídeos.")}</p>
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-3 rounded-2xl border border-[#D9E0EA] bg-white p-[18px]">
        <div className="flex flex-col gap-1">
          <h2 className="text-[15px] font-bold text-[#111827]">{translateInterfaceText("Por vídeo")}</h2>
          <p className="text-xs text-[#4B5563]">
            {translateInterfaceText("Visualizações únicas e plays únicos contam aparelhos diferentes no período. Clique num vídeo para ver a retenção.")}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-[#4B5563]">
                <th scope="col" className={th}>
                  {translateInterfaceText("Vídeo")}</th>
                <th scope="col" className={cn(th, 'text-right')}>
                  {translateInterfaceText("Visualizações")}</th>
                <th scope="col" className={cn(th, 'text-right')}>
                  {translateInterfaceText("Visualizações únicas")}</th>
                <th scope="col" className={cn(th, 'text-right')}>
                  {translateInterfaceText("Plays")}</th>
                <th scope="col" className={cn(th, 'text-right')}>
                  {translateInterfaceText("Plays únicos")}</th>
                <th scope="col" className={cn(th, 'text-right')}>
                  {translateInterfaceText("Taxa de play")}</th>
                <th scope="col" className={cn(th, 'text-right')}>
                  {translateInterfaceText("Retenção média")}</th>
              </tr>
            </thead>
            <tbody>
              {videos.map((video) => {
                const active = video.video_id === selected?.video_id;
                return (
                  <tr
                    key={video.video_id}
                    onClick={() => setSelectedId(video.video_id)}
                    className={cn(
                      'cursor-pointer border-b border-[#F1F5F9] last:border-0',
                      active ? 'bg-[#EEF4FF]' : 'hover:bg-[#F8FAFC]',
                    )}
                  >
                    <td className="px-2.5 py-2">
                      <button
                        type="button"
                        aria-label={translateInterfaceText("Ver retenção de {value0}", { value0: (video.name) })}
                        aria-pressed={active}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedId(video.video_id);
                        }}
                        className="flex min-w-0 items-center gap-2.5 text-left font-semibold text-[#111827]"
                      >
                        {video.frame ? (
                          <img src={video.frame} alt="" className="h-10 w-7 shrink-0 rounded object-cover" />
                        ) : (
                          <span className="flex h-10 w-7 shrink-0 items-center justify-center rounded bg-[#E5E7EB] text-[#6B7280]">
                            <Film className="h-3.5 w-3.5" aria-hidden="true" />
                          </span>
                        )}
                        <span className="truncate">{video.name}</span>
                      </button>
                    </td>
                    <td className={td}>{formatNumber(video.views)}</td>
                    <td className={td}>{formatNumber(video.unique_views)}</td>
                    <td className={td}>{formatNumber(video.plays)}</td>
                    <td className={td}>{formatNumber(video.unique_plays)}</td>
                    <td className={td}>{formatPercent(video.play_rate)}</td>
                    <td className={td}>
                      {formatPercent(video.avg_watched)} ({formatSeconds(video.avg_watched_seconds)})
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      {selected && <VideoRetentionChart video={selected} />}
    </div>
  );
}
